import { useState } from 'react';
import { ArrowRight, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import { toast } from 'sonner';
import { publicApi } from '@/lib/api';

const workshopTypes = [
  "Job Search/Cover Letters",
  "Building a LinkedIn Profile",
  "Business Etiquette",
  "Career Fair Strategies",
  "Career Portfolio",
  "Dress for Success",
  "Graduate School: To Go or Not To Go",
  "Internship Search Strategies",
  "Interviewing",
  "Job Search Strategies",
  "Job Shadowing",
  "Negotiating Job Offers",
  "Personal Branding",
  "Professional Communication",
  "Resume Critiquing",
  "Resume Writing/Cover Letters",
  "Social Media and the Job Search",
  "Writing a Personal Statement",
  "Other"
];

const durationOptions = [
  "45 minutes",
  "60 minutes",
  "90 minutes",
  "2 hours",
  "Half-day (4 hours)",
  "Full-day (8 hours)",
  "Custom duration"
];

const WorkshopRequestPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    website: '',
    workshopType: '',
    otherWorkshopType: '',
    message: '',
    duration: '',
    deliveryFormat: 'virtual',
    preferredDate: '',
    audienceSize: '',
    budgetRange: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const workshopTypeDisplay = formData.workshopType === 'Other' 
        ? `Other: ${formData.otherWorkshopType}` 
        : formData.workshopType;

      const submissionData = {
        form_type: 'workshop_request',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: `Workshop Request: ${workshopTypeDisplay}`,
        message: `
Organization: ${formData.organization}
Website: ${formData.website || 'Not provided'}
Workshop Type: ${workshopTypeDisplay}
Duration Needed: ${formData.duration}
Delivery Format: ${formData.deliveryFormat}
Preferred Date/Timeframe: ${formData.preferredDate || 'Flexible'}
Estimated Audience Size: ${formData.audienceSize || 'Not specified'}
Budget Range: ${formData.budgetRange || 'Not specified'}

Event Description:
${formData.message}
        `.trim(),
        page_origin: 'Workshop Request Form'
      };

      await publicApi.submitForm(submissionData);
      setSubmitted(true);
      toast.success('Workshop request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <PageBanner 
            title="Request a Workshop" 
            breadcrumbs={[
              { label: 'Services', path: '/services' },
              { label: 'Community Workshops', path: '/service/workshop' },
              { label: 'Request a Workshop' }
            ]} 
          />
          <section className="py-16">
            <div className="max-w-2xl mx-auto px-4 text-center">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-10">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-heading font-bold mb-4">Thank You!</h2>
                <p className="text-muted-foreground mb-6">
                  Your workshop request has been submitted successfully. Our team will review your request and get back to you within 1-2 business days to discuss the details.
                </p>
                <Button asChild>
                  <a href="/service/workshop">
                    Back to Workshops <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title="Request a Workshop" 
          breadcrumbs={[
            { label: 'Services', path: '/services' },
            { label: 'Community Workshops', path: '/service/workshop' },
            { label: 'Request a Workshop' }
          ]} 
        />

        <section className="py-16" data-testid="workshop-request-form-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                Tell Us About Your Workshop Needs
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Complete the form below and our team will work with you to customize the perfect workshop for your organization.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8 bg-white rounded-2xl shadow-lg p-8 border" data-testid="workshop-request-form">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-heading font-bold mb-4 pb-2 border-b">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      data-testid="workshop-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@organization.com"
                      data-testid="workshop-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(555) 123-4567"
                      data-testid="workshop-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization">Organization Name *</Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                      placeholder="Company/School/Organization"
                      data-testid="workshop-organization-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="website">Website (Optional)</Label>
                    <Input
                      id="website"
                      name="website"
                      type="url"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://www.yourorganization.com"
                      data-testid="workshop-website-input"
                    />
                  </div>
                </div>
              </div>

              {/* Workshop Details */}
              <div>
                <h3 className="text-lg font-heading font-bold mb-4 pb-2 border-b">Workshop Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="workshopType">Type of Workshop *</Label>
                    <Select 
                      value={formData.workshopType} 
                      onValueChange={(value) => handleSelectChange('workshopType', value)}
                      required
                    >
                      <SelectTrigger data-testid="workshop-type-select">
                        <SelectValue placeholder="Select a workshop topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {workshopTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.workshopType === 'Other' && (
                    <div>
                      <Label htmlFor="otherWorkshopType">Please specify the workshop topic *</Label>
                      <Input
                        id="otherWorkshopType"
                        name="otherWorkshopType"
                        value={formData.otherWorkshopType}
                        onChange={handleChange}
                        required={formData.workshopType === 'Other'}
                        placeholder="Describe the workshop topic you need"
                        data-testid="workshop-other-type-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="duration">Duration Needed *</Label>
                    <Select 
                      value={formData.duration} 
                      onValueChange={(value) => handleSelectChange('duration', value)}
                      required
                    >
                      <SelectTrigger data-testid="workshop-duration-select">
                        <SelectValue placeholder="Select workshop duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {durationOptions.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Delivery Format *</Label>
                    <RadioGroup 
                      value={formData.deliveryFormat} 
                      onValueChange={(value) => handleSelectChange('deliveryFormat', value)}
                      className="flex flex-wrap gap-4 mt-2"
                      data-testid="workshop-format-radio"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="virtual" id="virtual" />
                        <Label htmlFor="virtual" className="font-normal cursor-pointer">Virtual</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="in-person" id="in-person" />
                        <Label htmlFor="in-person" className="font-normal cursor-pointer">In-Person</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="hybrid" id="hybrid" />
                        <Label htmlFor="hybrid" className="font-normal cursor-pointer">Hybrid</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="preferredDate">Preferred Date/Timeframe</Label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        placeholder="e.g., March 2026, Spring semester"
                        data-testid="workshop-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="audienceSize">Estimated Audience Size</Label>
                      <Input
                        id="audienceSize"
                        name="audienceSize"
                        value={formData.audienceSize}
                        onChange={handleChange}
                        placeholder="e.g., 25-50 participants"
                        data-testid="workshop-audience-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="budgetRange">Budget Range (Optional)</Label>
                    <Select 
                      value={formData.budgetRange} 
                      onValueChange={(value) => handleSelectChange('budgetRange', value)}
                    >
                      <SelectTrigger data-testid="workshop-budget-select">
                        <SelectValue placeholder="Select budget range (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under $500">Under $500</SelectItem>
                        <SelectItem value="$500 - $1,000">$500 - $1,000</SelectItem>
                        <SelectItem value="$1,000 - $2,500">$1,000 - $2,500</SelectItem>
                        <SelectItem value="$2,500 - $5,000">$2,500 - $5,000</SelectItem>
                        <SelectItem value="$5,000+">$5,000+</SelectItem>
                        <SelectItem value="To be discussed">To be discussed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Event Description */}
              <div>
                <h3 className="text-lg font-heading font-bold mb-4 pb-2 border-b">Event Description</h3>
                <div>
                  <Label htmlFor="message">Tell us about your event or training needs *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Please provide details about your event, training objectives, audience background, any specific topics you'd like covered, and any other relevant information that will help us customize the workshop for your organization."
                    data-testid="workshop-message-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full" 
                disabled={loading}
                data-testid="workshop-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit Workshop Request'}
                <Send className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WorkshopRequestPage;
